import { useEffect } from 'react'
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom'

import { useAppSelector } from '@/hooks'
import { AdminPanel } from '@/pages/admin'
import { StandDetails } from '@/pages/admin/stand-details'
import { CancellationPolicy } from '@/pages/cancellation-policy'
import { BookShelf } from '@/pages/customer/book-shelf'
import { BookingSuccess } from '@/pages/customer/book-shelf/success'
import { Bookings } from '@/pages/customer/bookings'
import { ExtendBooking } from '@/pages/customer/extend-booking'
import { Overview } from '@/pages/customer/overview'
import { Products } from '@/pages/customer/products'
import { Profile } from '@/pages/customer/profile'
import { Recruit } from '@/pages/customer/recruit'
import { DataCenter } from '@/pages/data-center'
import { Faqs } from '@/pages/faqs'
import { ForgotPassword } from '@/pages/forgot-password'
import { Home } from '@/pages/home'
import { Impressum } from '@/pages/impressum'
import { Login } from '@/pages/login'
import { Search } from '@/pages/search'
import { StoreProfile } from '@/pages/store-profile'
import { SuperAdminPanel } from '@/pages/super-admin'
import { TermsAndConditions } from '@/pages/terms-and-conditions'
import { homePathForType } from '@/lib/home-path'
import { isStoreAdmin, isSuperAdmin } from '@/lib/roles'

const Routing = () => {
	const auth = useAppSelector(state => state.auth)

	return (
		<BrowserRouter>
			<ScrollToTop />
			<Routes>
				<Route path="/" element={<Home />} />
				<Route path="/stores/:storeRef" element={<StoreProfile />} />
				<Route path="/stores/:storeRef/book" element={<BookShelf />} />
				<Route
					path="/login"
					element={
						<PublicRoute auth={auth}>
							<Login />
						</PublicRoute>
					}
				/>
				<Route
					path="/forgot-password"
					element={
						<PublicRoute auth={auth}>
							<ForgotPassword />
						</PublicRoute>
					}
				/>
				<Route
					path="/super-admin"
					element={
						<ProtectedRoute customRedirect={!isSuperAdmin(auth.type)} auth={auth}>
							<SuperAdminPanel />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/admin-panel"
					element={
						<ProtectedRoute customRedirect={!isStoreAdmin(auth.type)} auth={auth}>
							<AdminPanel />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/stand-details/:id"
					element={
						<ProtectedRoute customRedirect={!isStoreAdmin(auth.type)} auth={auth}>
							<StandDetails />
						</ProtectedRoute>
					}
				/>
				<Route path="/customer">
					<Route
						path="overview"
						element={
							<ProtectedRoute customRedirect={auth.type !== 'user'} auth={auth}>
								<Overview />
							</ProtectedRoute>
						}
					/>
					<Route
						path="bookings"
						element={
							<ProtectedRoute customRedirect={auth.type !== 'user'} auth={auth}>
								<Bookings />
							</ProtectedRoute>
						}
					/>
					<Route
						path="products"
						element={
							<ProtectedRoute customRedirect={auth.type !== 'user'} auth={auth}>
								<Products />
							</ProtectedRoute>
						}
					/>
					<Route
						path="extend-booking"
						element={
							<ProtectedRoute customRedirect={auth.type !== 'user'} auth={auth}>
								<ExtendBooking />
							</ProtectedRoute>
						}
					/>
					<Route
						path="profile"
						element={
							<ProtectedRoute customRedirect={auth.type !== 'user'} auth={auth}>
								<Profile />
							</ProtectedRoute>
						}
					/>
					<Route
						path="recruit"
						element={
							<ProtectedRoute customRedirect={auth.type !== 'user'} auth={auth}>
								<Recruit />
							</ProtectedRoute>
						}
					/>
					<Route
						path="regal-mieten"
						element={<Navigate to="/" replace />}
					/>
				</Route>
				<Route path="/booking-success" element={<BookingSuccess />} />
				<Route path="/suchen" element={<Search />} />
				<Route path="/widerruf" element={<CancellationPolicy />} />
				<Route path="/letter" element={<TermsAndConditions />} />
				<Route path="/datenschutz" element={<DataCenter />} />
				<Route path="/faqs" element={<Faqs />} />
				<Route
					path="/admin-profile"
					element={
						<ProtectedRoute customRedirect={!isStoreAdmin(auth.type)} auth={auth}>
							<Profile />
						</ProtectedRoute>
					}
				/>
				<Route path="/kontakt" element={<Impressum />} />
			</Routes>
		</BrowserRouter>
	)
}

type RouteProps = {
	auth?: AuthState
	redirectPath?: string
	children: JSX.Element
	customRedirect?: boolean
}

const ProtectedRoute = ({
	auth,
	children,
	customRedirect,
	redirectPath = '/login',
}: RouteProps) => {
	if (!auth?.type || customRedirect) {
		return <Navigate to={redirectPath} replace={true} />
	}

	return children
}

const PublicRoute = ({ auth, children }: RouteProps) => {
	if (auth?.type) {
		return <Navigate to={homePathForType(auth.type)} replace />
	}

	return children
}

const ScrollToTop = () => {
	const { pathname } = useLocation()
	useEffect(() => {
		window.scrollTo(0, 0)
	}, [pathname])

	return null
}

export default Routing
